import { Modal } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
  useCreatePosition,
  usePositionsQuery,
  useUpdatePosition,
} from "../api/queries";
import { PositionForm } from "../ui/PositionForm";
import { PositionSalaryBandsModal } from "../ui/PositionSalaryBandsModal";
import { PositionsFilter } from "../ui/PositionsFilter";
import { PositionsTable } from "../ui/PositionsTable";
import { PositionsToolbar } from "../ui/PositionsToolbar";

export function PositionsPage() {
  const nav = useNavigate({ from: "/app/positions" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/positions" });

  const list = usePositionsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["positions:create"]);
  const createPosition = useCreatePosition();
  const editModal = useDisclosure<{
    id: string;
    title: string;
    departmentId: string;
    parentId: string | null;
    level: number;
    maxEmployees: number | null;
  }>();
  const updatePosition = useUpdatePosition(editModal.data?.id ?? "");
  const createModal = useDisclosure();
  const bandsModal = useDisclosure<{ id: string; title: string }>();

  return (
    <>
      <Header />

      <Main>
        <PositionsToolbar canManage={!!canManage} onCreate={createModal.open} />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <PositionsFilter />

          <PositionsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(p) =>
              editModal.openWith({
                id: p.id,
                title: p.title,
                departmentId: p.departmentId,
                parentId: p.parentId,
                level: p.level,
                maxEmployees: p.maxEmployees,
              })
            }
            onSalaryBands={(p) =>
              bandsModal.openWith({ id: p.id, title: p.title })
            }
          />
        </div>

        <Modal
          open={!!createModal.isOpen}
          onOpenChange={createModal.toggle}
          title="ສ້າງຕໍາແໜ່ງ"
          size="sm"
        >
          <PositionForm
            onSubmit={async (vals) => {
              await createPosition.mutateAsync({
                title: vals.title,
                departmentId: vals.departmentId,
                parentId: vals.parentId ?? null,
                level: vals.level,
                maxEmployees: vals.maxEmployees ?? null,
              });
              createModal.close();
            }}
            submitting={createPosition.isPending}
          />
        </Modal>

        <Modal
          open={!!editModal.isOpen}
          onOpenChange={editModal.toggle}
          title="ແກ້ໄຂຕໍາແໜ່ງ"
          size="sm"
        >
          <PositionForm
            initialValues={editModal.data ?? undefined}
            excludePositionId={editModal.data?.id}
            onSubmit={async (vals) => {
              await updatePosition.mutateAsync({
                title: vals.title,
                departmentId: vals.departmentId,
                parentId: vals.parentId ?? null,
                level: vals.level,
                maxEmployees: vals.maxEmployees ?? null,
              });
              editModal.close();
            }}
            submitting={updatePosition.isPending}
          />
        </Modal>

        <PositionSalaryBandsModal
          open={!!bandsModal.isOpen}
          onOpenChange={(v) => {
            if (!v) bandsModal.close();
            else bandsModal.open();
          }}
          positionId={bandsModal.data?.id ?? ""}
          positionTitle={bandsModal.data?.title ?? ""}
        />
      </Main>
    </>
  );
}
